/**
 * design_handoff_billing — "Change plan" is its own route, not a section
 * that unfolds under the summary card. Owns the plans fetch, the loading
 * state and the back link. Business logic (action labels, downgrade-blocker
 * gate, checkout vs. customer-portal submit path) ports the working logic
 * already in component/Subscription/SubscriptionsPricingTable.js — this
 * page only wraps it in the handoff's own chrome and column styling.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageTemplate from "../templates/PageTemplate";
import PageHeader from "../templates/AppShell/PageHeader/PageHeader";
import Card from "../theme/untitled/Card/Card";
import Select from "../theme/untitled/Select/Select";
import Icon from "../theme/untitled/icons/Icon";
import Band from "./BillingPage/Band";
import PlanColumn from "./ChangePlanPage/PlanColumn";
import SkeletonColumn from "./ChangePlanPage/SkeletonColumn";
import IntervalToggle from "./ChangePlanPage/IntervalToggle";
import DowngradeModal from "./ChangePlanPage/DowngradeModal";
import { getPreferredCurrencyForLocale } from "./ChangePlanPage/currencyRegion";
import { useSubscriptionPlansStore } from "../../../hooks/redux/subscriptionPlans";
import { useSubscriptionStore } from "../../../hooks/redux/subscription";
import { useCurrentOrganizationStore } from "../../../hooks/redux/currentOrganization";
import { useUserStore } from "../../../hooks/redux/user";
import { hasSubscription } from "../helpers/organization";
import { isNewPlanIsBelowUsage } from "../helpers/subscription";
import { formatError } from "../../../helpers/errors";
import { PLANS } from "../../../consts";
import { FreePlanConfig } from "../consts/pricing";
import styles from "./ChangePlanPage.module.scss";

const findPrice = (product, currency, interval) =>
  product?.prices?.[currency]?.find((p) => p.recurring?.interval === interval);

// Free never comes back from the plans endpoint (Stripe only knows about
// paid products) but the handoff wants it as a real first column, not a
// separate summary — synthesize the same {product, price} shape the paid
// columns use so PlanColumn doesn't need a free-specific code path.
const FREE_PRODUCT = {
  id: "free",
  name: "Forever Free",
  description: "Try the product on one site.",
  marketing_features: FreePlanConfig.marketing_features,
  metadata: { app_plan_code: PLANS.free },
};
const freePrice = (currency) => ({
  unit_amount: 0,
  currency,
  livemode: true,
  recurring: { interval: "month" },
});

const ChangePlanPage = () => {
  const { currentOrganization } = useCurrentOrganizationStore();
  const { user } = useUserStore();
  const { subscriptionPlans, fetchPlans } = useSubscriptionPlansStore();
  const { subscription, createStripeCheckoutSession, fetchCustomerPortalLink } =
    useSubscriptionStore();

  const [interval, setInterval] = useState("month");
  const [currency, setCurrency] = useState(() =>
    getPreferredCurrencyForLocale(navigator.language)
  );
  const [downgrade, setDowngrade] = useState(null); // { plan, price, blocked }
  const [lastAttempt, setLastAttempt] = useState(null); // { plan, price }

  useEffect(() => {
    fetchPlans();
  }, []);

  useEffect(() => {
    if (subscription.startCheckoutSessionSuccess && subscription.checkoutSession) {
      window.open(subscription.checkoutSession.url, "_self");
    }
  }, [subscription.startCheckoutSessionSuccess, subscription.checkoutSession]);

  useEffect(() => {
    if (subscription.stripeBillingPortalLinkFetchSuccess && subscription.stripeBillingPortalLink) {
      window.open(subscription.stripeBillingPortalLink.url, "_self");
    }
  }, [subscription.stripeBillingPortalLinkFetchSuccess]);

  const currencies = useMemo(() => {
    if (!subscriptionPlans.collection.length) return [];
    return Object.keys(subscriptionPlans.collection[0].prices || {});
  }, [subscriptionPlans.collection]);

  useEffect(() => {
    if (currencies.length && !currencies.includes(currency)) {
      setCurrency(currencies.includes("usd") ? "usd" : currencies[0]);
    }
  }, [currencies]);

  const currentSubscription = hasSubscription(currentOrganization) || {};
  const isFree = !currentSubscription.code || currentSubscription.code === PLANS.free;
  // Account-wide, not per-workspace: a user only gets one trial ever, across
  // every workspace they created (see hasCreatorUsedTrialElsewhere on the
  // backend) — this workspace's own plan history isn't enough to know that.
  const hasUsedTrial = Boolean(user.model?.has_used_trial);

  const isBusy =
    subscription.startCheckoutSession ||
    subscription.stripeBillingPortalLinkFetch ||
    subscription.stripeCustomerSessionFetch;

  const onSubmit = useCallback(
    (price, plan) => {
      setLastAttempt({ price, plan });
      if (isFree) {
        createStripeCheckoutSession(price.id);
      } else {
        fetchCustomerPortalLink("subscription_update");
      }
    },
    [isFree, createStripeCheckoutSession, fetchCustomerPortalLink]
  );

  const onRetry = useCallback(() => {
    if (lastAttempt) onSubmit(lastAttempt.price, lastAttempt.plan);
  }, [lastAttempt, onSubmit]);

  const onSelectPrice = useCallback(
    (price, plan) => {
      if (isBusy) return;
      const planCode = plan.metadata?.app_plan_code;
      const planKeys = Object.values(PLANS);
      const currentIndex = planKeys.indexOf(currentSubscription.code);
      const newIndex = planKeys.indexOf(planCode);
      const isDowngrade =
        !isFree && !currentSubscription.is_trial && currentIndex >= 0 && currentIndex > newIndex;

      if (isDowngrade) {
        setDowngrade({
          plan,
          price,
          blocked: isNewPlanIsBelowUsage(plan, currentOrganization),
        });
        return;
      }
      onSubmit(price, plan);
    },
    [isBusy, isFree, currentSubscription, currentOrganization, onSubmit]
  );

  const products = useMemo(
    () => subscriptionPlans.collection.filter((p) => p.prices?.[currency]?.length),
    [subscriptionPlans.collection, currency]
  );

  const discount = subscriptionPlans.coupon;
  const isLoading = subscriptionPlans.fetch;

  const allProducts = useMemo(() => [FREE_PRODUCT, ...products], [products]);

  const columns = allProducts.map((plan, idx) => {
    const isFreeColumn = plan === FREE_PRODUCT;
    const monthlyPrice = isFreeColumn ? freePrice(currency) : findPrice(plan, currency, "month");
    const yearlyPrice = isFreeColumn ? null : findPrice(plan, currency, "year");
    const activePrice = isFreeColumn ? monthlyPrice : interval === "year" ? yearlyPrice : monthlyPrice;
    const planCode = plan.metadata?.app_plan_code;
    const isSamePlan = !isFree && !currentSubscription.is_trial && planCode === currentSubscription.code;
    // A plan match alone isn't "current" — the subscription is also billed
    // on one specific interval (is_annual), so the same plan tier viewed
    // under the OTHER interval toggle is a real switch, not a no-op.
    const isSameInterval = (interval === "year") === Boolean(currentSubscription.is_annual * 1);
    const isCurrent = isFreeColumn ? isFree : isSamePlan && isSameInterval;

    let actionLabel = hasUsedTrial ? "Subscribe" : "Start free trial";
    if (isCurrent) {
      actionLabel = "Current plan";
    } else if (isFreeColumn) {
      actionLabel = "Downgrade to Free";
    } else if (isSamePlan) {
      actionLabel = interval === "year" ? "Switch to yearly billing" : "Switch to monthly billing";
    } else if (!isFree && !currentSubscription.is_trial) {
      const planKeys = Object.values(PLANS);
      const currentIndex = planKeys.indexOf(currentSubscription.code);
      const newIndex = planKeys.indexOf(planCode);
      actionLabel = newIndex > currentIndex ? "Upgrade" : "Downgrade";
    }

    return (
      <PlanColumn
        key={plan.id}
        plan={plan}
        monthlyPrice={monthlyPrice}
        yearlyPrice={yearlyPrice}
        interval={interval}
        currency={currency}
        discount={discount}
        isCurrent={isCurrent}
        isBest={!isFreeColumn && idx === 2}
        actionLabel={actionLabel}
        disabled={isBusy || isCurrent || !activePrice}
        onAction={onSelectPrice}
      />
    );
  });

  const isSandbox = products[0] && !findPrice(products[0], currency, interval)?.livemode;

  const checkoutErrorMessage = subscription.startCheckoutSessionError
    ? formatError(
        subscription.startCheckoutSessionError,
        "Stripe did not return a checkout session, so nothing was charged and your plan has not changed."
      ).join(" ")
    : null;

  return (
    <PageTemplate>
      <PageHeader
        breadcrumbs={[
          { label: "Billing", to: "/organization-settings/billing" },
          { label: "Change plan" },
        ]}
        title="Change plan"
        subtitle="Your plan sets how many widgets, websites, team members and monthly views the workspace can have. Changes take effect immediately."
      />
      <div className={styles.body}>
        <Card className={styles.card}>
          {isSandbox && (
            <Band kind="gray" icon={<Icon name="Beaker02" size={20} />} title="Test mode">
              These plans come from Stripe test data (<code>livemode: false</code>), so the
              prices are not real and no card will be charged.
            </Band>
          )}
          {discount && (
            <Band kind="info" icon={<Icon name="Gift01" size={20} />} title="Discount applied">
              {discount.percent_off}% off
              {discount.duration === "repeating" ? " every payment" : " your first payment"}.
              It is applied at checkout, and the prices below already include it.
            </Band>
          )}
          {checkoutErrorMessage && (
            <Band
              kind="err"
              icon={<Icon name="AlertCircle" size={20} />}
              title="We could not start checkout"
              action={{ label: "Try again", onClick: onRetry, disabled: isBusy }}
            >
              {checkoutErrorMessage}
            </Band>
          )}
          {subscription.stripeBillingPortalLinkFetchError && (
            <Band
              kind="err"
              icon={<Icon name="AlertCircle" size={20} />}
              title="We could not open the billing portal"
              action={{ label: "Try again", onClick: onRetry, disabled: isBusy }}
            >
              Stripe did not return a billing portal session, so nothing was charged and
              your plan has not changed.
            </Band>
          )}
          <div className={styles.toolbar}>
            <IntervalToggle interval={interval} onChange={setInterval} />
            {currencies.length > 1 && (
              <Select
                className={styles.currency}
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                options={currencies.map((c) => ({ value: c, label: c.toUpperCase() }))}
              />
            )}
          </div>
          <div className={styles.cols}>
            {isLoading
              ? [0, 1, 2, 3].map((i) => <SkeletonColumn key={i} />)
              : columns}
          </div>
        </Card>
      </div>
      {downgrade && (
        <DowngradeModal
          plan={downgrade.plan}
          price={downgrade.price}
          blocked={downgrade.blocked}
          currentOrganization={currentOrganization}
          currentSubscription={currentSubscription}
          onClose={() => setDowngrade(null)}
          onConfirm={() => {
            onSubmit(downgrade.price, downgrade.plan);
            setDowngrade(null);
          }}
        />
      )}
    </PageTemplate>
  );
};

export default ChangePlanPage;
